import { useState } from 'react';
import { Badge, Menu } from '../../components/ui';
import {
  formatDate,
  InlineTextInput,
  isOverdue,
  memberLabel,
  statusTone,
} from '../listView/TaskTable';
import { TASK_PRIORITY_LABELS } from '../tasks/types';
import { FieldChips } from '../fields/FieldPicker';
import { swimlanes } from './swimlanes';
import type { FieldChip } from '../fields/fieldChips';
import type { MenuItem } from '../../components/ui';
import type { ProjectMemberWithProfile, ProjectStatus } from '../projects/types';
import type { SavedViewSort } from '../savedViews/types';
import type { Section } from '../sections/types';
import type { Task } from '../tasks/types';
import type { SectionStatusMap } from './sectionPipeline';
import type { BoardColumn, SubtaskProgress } from './boardLens';
import type { DragEvent } from 'react';

/**
 * Sections down, pipeline across.
 *
 * Grouping by Section made the sections the columns, so the pipeline vanished:
 * you could see which bucket a piece was in and nothing about how far along it
 * was. Grouping by Status put one pipeline over the whole project, with long
 * form and Facebook work interleaved in every column. Neither answers "where
 * is my short-form work up to".
 *
 * The row says which kind of work; the column says how far along. Each row
 * draws its OWN columns — see swimlanes.ts.
 *
 * Dropping a card writes BOTH: the lane it landed in is the section, the
 * column is the status. That is the one thing this view can do that neither
 * grouped board can, and it is why a drop handler here takes two ids.
 */
export function SwimlaneBoard({
  tasks,
  statuses,
  sections,
  sectionStatuses,
  sort,
  members,
  progress,
  fieldChips,
  canEdit,
  onOpenTask,
  onMove,
  onFocusSection,
  onRenameTask,
}: {
  tasks: Task[];
  statuses: ProjectStatus[];
  sections: Section[];
  sectionStatuses: SectionStatusMap;
  sort: SavedViewSort;
  members: ProjectMemberWithProfile[];
  progress: Record<string, SubtaskProgress>;
  fieldChips: Map<string, FieldChip[]>;
  canEdit: boolean;
  onOpenTask: (task: Task) => void;
  /** Writes the section AND the status in one go. Null clears either. */
  onMove: (taskId: string, sectionId: string | null, statusId: string | null) => void;
  /** Clicking a lane name drops into just that section. */
  onFocusSection: (sectionId: string | null) => void;
  /** Renames the task on a card. Absent means no Rename item in the menu. */
  onRenameTask?: ((task: Task, changes: { title: string }) => void) | undefined;
}) {
  const lanes = swimlanes(sections, tasks, statuses, sectionStatuses, sort);

  if (lanes.length === 0) {
    return (
      <p className="board-column-empty">
        This project has no sections yet. Add one to see work grouped by kind.
      </p>
    );
  }

  return (
    <div className="swimlanes">
      {lanes.map((lane) => (
        <section key={lane.key} className="swimlane" aria-label={`${lane.name} lane`}>
          <header className="swimlane-head">
            <button
              type="button"
              className="swimlane-name"
              onClick={() => onFocusSection(lane.sectionId)}
              title={`Show only ${lane.name}`}
            >
              {lane.name}
            </button>
            <span className="swimlane-count">
              {lane.taskCount} {lane.taskCount === 1 ? 'task' : 'tasks'}
            </span>
          </header>

          <div className="swimlane-columns">
            {lane.columns.map((column) => (
              <LaneColumn
                key={column.key}
                column={column}
                laneSectionId={lane.sectionId}
                members={members}
                progress={progress}
                fieldChips={fieldChips}
                canEdit={canEdit}
                onOpenTask={onOpenTask}
                onMove={onMove}
                moveItems={(task) => moveItemsFor(task, lanes, lane.sectionId, onMove)}
                onRenameTask={onRenameTask}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * Where a card can go from here, as a menu.
 *
 * Every column of its OWN lane, because moving across lanes from a menu would
 * need a two-level list and the drag already does that better. Keyboard users
 * get the pipeline; changing section stays where it always was, on the task.
 */
function moveItemsFor(
  task: Task,
  lanes: ReturnType<typeof swimlanes>,
  sectionId: string | null,
  onMove: (taskId: string, sectionId: string | null, statusId: string | null) => void,
): MenuItem[] {
  const lane = lanes.find((entry) => entry.sectionId === sectionId);
  return (lane?.columns ?? [])
    .filter((column) => column.groupId !== task.status_id)
    .map((column) => ({
      label: `Move to ${column.name}`,
      onSelect: () => onMove(task.id, sectionId, column.groupId),
    }));
}

function LaneColumn({
  column,
  laneSectionId,
  members,
  progress,
  fieldChips,
  canEdit,
  onOpenTask,
  onMove,
  moveItems,
  onRenameTask,
}: {
  column: BoardColumn;
  laneSectionId: string | null;
  members: ProjectMemberWithProfile[];
  progress: Record<string, SubtaskProgress>;
  fieldChips: Map<string, FieldChip[]>;
  canEdit: boolean;
  onOpenTask: (task: Task) => void;
  onMove: (taskId: string, sectionId: string | null, statusId: string | null) => void;
  moveItems: (task: Task) => MenuItem[];
  onRenameTask?: ((task: Task, changes: { title: string }) => void) | undefined;
}) {
  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    const taskId = event.dataTransfer.getData('text/plain');
    if (taskId !== '' && canEdit) {
      // Both, from one gesture. The lane is the section and the column is the
      // status — the whole reason this view exists.
      onMove(taskId, laneSectionId, column.groupId);
    }
  }

  return (
    <div
      className="swimlane-column"
      onDragOver={(event) => {
        if (canEdit) {
          event.preventDefault();
        }
      }}
      onDrop={onDrop}
    >
      <header className="swimlane-column-head">
        <Badge tone={statusTone(column.colorKey)}>{column.name}</Badge>
        <span className="swimlane-column-count">{column.tasks.length}</span>
      </header>
      <ul className="swimlane-cards">
        {column.tasks.map((task) => (
          <LaneCard
            key={task.id}
            task={task}
            members={members}
            progress={progress[task.id]}
            fieldChips={fieldChips.get(task.id) ?? []}
            canEdit={canEdit}
            onOpen={() => onOpenTask(task)}
            moveItems={moveItems(task)}
            onRename={onRenameTask}
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * One card in a lane.
 *
 * Lifted out of the map above when renaming arrived. Editing state has to live
 * somewhere per card, and a hook cannot live inside a map callback, so the
 * choice was a component or a lane-wide "which card is being renamed" variable
 * — and the second one is the same state with a worse name.
 *
 * Deliberately its own component rather than a shared one with the status
 * board's BoardCard: the two draw different things (no comment or file counts
 * here, and the meta line is a separate Meta component), and merging them to
 * share thirty lines would mean a card that takes both sets of props and uses
 * half of them.
 */
function LaneCard({
  task,
  members,
  progress,
  fieldChips,
  canEdit,
  onOpen,
  moveItems,
  onRename,
}: {
  task: Task;
  members: ProjectMemberWithProfile[];
  progress: SubtaskProgress | undefined;
  fieldChips: FieldChip[];
  canEdit: boolean;
  onOpen: () => void;
  moveItems: MenuItem[];
  onRename?: ((task: Task, changes: { title: string }) => void) | undefined;
}) {
  const [renaming, setRenaming] = useState(false);

  const canRename = canEdit && onRename !== undefined;
  const menuItems: MenuItem[] = canRename
    ? [{ label: 'Rename', onSelect: () => setRenaming(true) }, ...moveItems]
    : moveItems;

  // Same reasoning as the status board: the card is one click target that
  // opens the task, so the input replaces it rather than nesting inside it.
  if (renaming && onRename !== undefined) {
    return (
      <li className="board-card">
        <div className="board-card-renaming">
          <InlineTextInput
            value={task.title}
            inputLabel={`Title of ${task.title}`}
            allowEmpty={false}
            onSave={(next) => onRename(task, { title: next })}
            onClose={() => setRenaming(false)}
          />
        </div>
      </li>
    );
  }

  return (
    <li className="board-card">
      <button
        type="button"
        className="board-card-button"
        aria-label={`Open task ${task.title}`}
        draggable={canEdit}
        onDragStart={(event) => event.dataTransfer.setData('text/plain', task.id)}
        onClick={onOpen}
      >
        <span
          className={
            task.is_completed ? 'board-card-title board-card-title--completed' : 'board-card-title'
          }
        >
          {task.title}
        </span>
        <Meta task={task} members={members} progress={progress} />
        <FieldChips chips={fieldChips} />
      </button>
      {!canEdit || menuItems.length === 0 ? null : (
        <div className="board-card-menu">
          <Menu
            trigger={<span aria-hidden="true">•••</span>}
            triggerLabel={`Actions for ${task.title}`}
            items={menuItems}
          />
        </div>
      )}
    </li>
  );
}

/** The same three facts the grouped board's cards carry, and no more. */
function Meta({
  task,
  members,
  progress,
}: {
  task: Task;
  members: ProjectMemberWithProfile[];
  progress: SubtaskProgress | undefined;
}) {
  const assignee = members.find((member) => member.user_id === task.assignee_id);
  const overdue = isOverdue(task);
  const parts = [
    assignee ? memberLabel(assignee) : null,
    task.due_date === null ? null : `${formatDate(task.due_date)}${overdue ? ' · Overdue' : ''}`,
    task.priority === null ? null : TASK_PRIORITY_LABELS[task.priority],
    progress && progress.total > 0 ? `${progress.done}/${progress.total}` : null,
  ].filter((part): part is string => part !== null);

  if (parts.length === 0) {
    return null;
  }
  return <span className="board-card-meta">{parts.join(' · ')}</span>;
}
